import React, { useState, useEffect } from "react";
import { Trash2, X, Plus } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import SearchableCodeSelect from "../../../common/SearchableCodeSelect";

// Shape from CE's CAPIFilterSettings_PayrollUnions (apifilters.h):
// a single MAPRANGE(unions, 0, 1), no flags — same plain range shape as
// Payroll Departments, and confirmed against a live GET
// /group/{uuid}/filters payload to use "from"/"to" (not "from_code"/"to_code").
function blankRule() {
  return {
    filter_type: "include",
    prunion: { from: "", to: "" },
  };
}

export default function PayrollUnionsFilterModal({
  open,
  groupUuid,
  companyUuid,
  filter,
  onSave,
  onClose,
  readOnly = false,
}) {
  const [rules, setRules] = useState([blankRule()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const existing = filter?.rules;
    setRules(
      existing && existing.length > 0
        ? existing.map((r) => ({
            filter_type: r.filter_type || "include",
            prunion: {
              from: r.prunion?.from ?? "",
              to: r.prunion?.to ?? "",
            },
          }))
        : [blankRule()]
    );
    setError(null);
  }, [open, filter]);

  if (!open) return null;

  function updateRule(index, field, value) {
    setRules((prev) =>
      prev.map((r, i) =>
        i === index ? { ...r, prunion: { ...r.prunion, [field]: value } } : r
      )
    );
  }

  function updateFilterType(index, value) {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, filter_type: value } : r))
    );
  }

  function removeRule(index) {
    setRules((prev) =>
      prev.length === 1 ? [blankRule()] : prev.filter((_, i) => i !== index)
    );
  }

  function handleSave() {
    const cleanedRules = rules.map((r) => ({
      filter_type: r.filter_type,
      prunion: {
        from: r.prunion.from || null,
        to: r.prunion.to || null,
      },
    }));

    onSave({ resource: "prunion", mode: "filtered", rules: cleanedRules });
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div className="jfm-container" onClick={(e) => e.stopPropagation()}>
        <div className="jfm-header">
          <span className="jfm-title">Payroll Unions Filters</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <fieldset className="jfm-fieldset" disabled={readOnly}>
            {rules.map((rule, idx) => (
              <div key={idx} className="efm-row">
                <select
                  className="jfm-type-select"
                  value={rule.filter_type}
                  onChange={(e) => updateFilterType(idx, e.target.value)}
                >
                  <option value="include">Include</option>
                  <option value="exclude">Exclude</option>
                </select>

                <div className="efm-fields">
                  <div className="efm-inputs-row">
                    <div className="efm-field">
                      <label>From Union</label>
                      <SearchableCodeSelect
                        value={rule.prunion.from}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={FieldConfigurationAPI.getUnions}
                        formatOptionLabel={(option) =>
                          option.name
                            ? `${option.code} - ${option.name}`
                            : option.code
                        }
                        renderOption={(option) => (
                          <>
                            <div className="searchable-code-select__option-code">
                              {option.code}
                            </div>
                            <div className="searchable-code-select__option-name">
                              {option.name}
                            </div>
                          </>
                        )}
                        emptyMessage="No unions found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "from", code)}
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Union</label>
                      <SearchableCodeSelect
                        value={rule.prunion.to}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={FieldConfigurationAPI.getUnions}
                        formatOptionLabel={(option) =>
                          option.name
                            ? `${option.code} - ${option.name}`
                            : option.code
                        }
                        renderOption={(option) => (
                          <>
                            <div className="searchable-code-select__option-code">
                              {option.code}
                            </div>
                            <div className="searchable-code-select__option-name">
                              {option.name}
                            </div>
                          </>
                        )}
                        emptyMessage="No unions found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "to", code)}
                      />
                    </div>
                  </div>
                </div>

                {!readOnly && (
                  <button
                    className="jfm-delete"
                    onClick={() => removeRule(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </fieldset>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          {readOnly ? (
            <div />
          ) : (
            <button
              className="jfm-add-row"
              onClick={() => setRules((prev) => [...prev, blankRule()])}
            >
              <Plus size={14} /> Add Row
            </button>
          )}
          <div className="jfm-footer-actions">
            {readOnly ? (
              <button className="jfm-cancel" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="jfm-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="jfm-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
